import { EntityType } from '@frontend/common';
import { IdentificationClient } from '@frontend/identification/api';
import { IdentificationType } from '@frontend/identification/types';
import { IoTSelectV2 } from '@frontend/iot/select';
import { IoT, IoTBrand } from '@frontend/iot/types';
import { DefaultModal, ModalType } from '@frontend/modal';
import { ToastUtil } from '@frontend/toast-utils';
import { useState } from 'react';
import { BsAndroid2 } from 'react-icons/bs';
import { LuCpu, LuPlus, LuX } from 'react-icons/lu';

import defaultLogo from '../assets/images/telloport_logo_horizontal.png';
import AccountMembership from './account-membership/account-membership.component';
import IotDashboard from './iot-dashboard/iot-dashboard.component';

export interface IoTTab extends IoT {
    identifier: string;
}

export function App() {
    const [tabs, setTabs] = useState<IoTTab[]>([]);
    const [activeTab, setActiveTab] = useState<IoTTab | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedIoTId, setSelectedIoTId] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);

    const handleAddIoT = () => {
        setSelectedIoTId(null);
        setSubmitted(false);
        setShowModal(true);
    };

    const handleIoTSubmit = async (iot: IoT | null) => {
        setSubmitted(true);
        if (!iot) return;

        const existingTab = tabs.find((tab) => tab.id === iot.id);
        if (existingTab) {
            setActiveTab(existingTab);
            setShowModal(false);
            return;
        }

        const identifications = await IdentificationClient.fetchIdentifications({ entity_id: iot.id, entity_type: EntityType.IOT });
        const identifier = identifications.results.find((identification) => identification.type === IdentificationType.IOT);
        if (!identifier) {
            ToastUtil.error(`No identifier found for IoT: ${iot.name}`);
            console.error('No identifier found for IoT:', iot);
            return;
        }
        // Create new tab
        const newTab: IoTTab = { ...iot, identifier: identifier.key };

        setTabs([...tabs, newTab]);
        setActiveTab(newTab);
        setShowModal(false);
    };

    const handleCloseTab = (tabId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const newTabs = tabs.filter((tab) => tab.id !== tabId);
        setTabs(newTabs);

        // If closing active tab, switch to another tab
        if (activeTab?.id === tabId && newTabs.length > 0) {
            setActiveTab(newTabs[0]);
        } else if (newTabs.length === 0) {
            setActiveTab(null);
        }
    };

    return (
        <div className="bg-background-default flex h-screen w-screen flex-row overflow-hidden font-['Montserrat']">
            {/* Platform-style sidebar */}
            <div className='flex h-dvh shrink-0 items-stretch'>
                <div className='bg-brand-primary w-1.5 self-stretch' />
                <div className='bg-background-surface border-border-default z-50 flex h-full w-64 flex-col border-r'>
                    {/* Logo */}
                    <div className='flex w-full items-center justify-center p-8'>
                        <img
                            src={defaultLogo}
                            className='object-contain'
                            alt='Telloport'
                        />
                    </div>

                    {/* Device list */}
                    <div className='border-border-default flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto border-t p-3'>
                        {tabs.length === 0 ? (
                            <p className='text-brand-muted px-3 py-2 text-xs'>No devices added yet</p>
                        ) : (
                            tabs.map((tab) => {
                                const DeviceIcon = tab.brand === IoTBrand.ANDROID ? BsAndroid2 : LuCpu;
                                const isActive = activeTab?.id === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab)}
                                        className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${isActive ? 'bg-brand-primary text-always-white' : 'text-brand-muted hover:bg-brand-light'}`}>
                                        <div className='flex min-w-0 items-center gap-2'>
                                            <DeviceIcon className='size-4 shrink-0' />
                                            <span className='truncate'>{tab.name}</span>
                                        </div>
                                        <span
                                            role='button'
                                            aria-label='Remove device'
                                            onClick={(e) => handleCloseTab(tab.id, e)}
                                            className={`shrink-0 rounded p-0.5 ${isActive ? 'hover:bg-white/20' : 'hover:bg-brand-muted/10'}`}>
                                            <LuX className='size-3.5' />
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* Add IoT button */}
                    <div className='border-border-default border-t p-3'>
                        <button
                            onClick={handleAddIoT}
                            className='btn btn-brand-primary mb-0 flex w-full items-center justify-center gap-2'>
                            <LuPlus className='size-4' />
                            Add IoT Device
                        </button>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <main
                className='no-scrollbar flex h-screen min-w-0 flex-1 flex-col overflow-hidden'
                id='app-content'>
                {/* Platform-style header */}
                <header className='bg-background-surface border-border-default sticky top-0 z-50 flex h-16 w-full shrink-0 items-center justify-between border-b pl-6'>
                    <span className='text-foreground-default text-base font-semibold'>{activeTab ? activeTab.name : 'IoT Emulator'}</span>
                    <AccountMembership />
                </header>

                {/* Content area */}
                <div className='no-scrollbar flex-1 overflow-auto p-6'>
                    {activeTab ? (
                        <IotDashboard iot={activeTab} />
                    ) : (
                        <div className='flex h-full flex-col items-center justify-center gap-3'>
                            <LuCpu className='text-brand-disabled size-12' />
                            <p className='text-brand-muted text-base font-medium'>No IoT device selected</p>
                            <p className='text-brand-muted text-sm'>Use the sidebar to add a device</p>
                        </div>
                    )}
                </div>

                <DefaultModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    type={ModalType.PROMPT}
                    id='iot-select-modal'>
                    <div className='bg-background-surface rounded-lg p-6'>
                        <h3 className='mb-4 text-xl font-semibold'>Select IoT Device</h3>
                        <IoTSelectV2
                            onChange={setSelectedIoTId}
                            onChangeObject={handleIoTSubmit}
                            value={selectedIoTId}
                            submitted={submitted}
                            label='IoT Device'
                            required
                        />
                        <div className='mt-4 flex gap-2'>
                            <button
                                onClick={() => setShowModal(false)}
                                className='btn mb-0'>
                                Cancel
                            </button>
                        </div>
                    </div>
                </DefaultModal>
            </main>
        </div>
    );
}

export default App;
